import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from "axios";
import * as z from "zod";
import { authToken } from "@infrastructure/clients/http/authToken";
import { handleError } from "@shared/utils/errors/apiErrorHandler";
import { AuthService } from "@domain/services/auth.service";
import { AuthStore } from "@infrastructure/stores/auth.store";
import { analytics } from "@shared/utils/analytics";
import {
  logHttpError,
  logHtmlResponseWarning,
  logSchemaValidationError,
  logInterceptorError,
  logBackgroundReauthError,
} from "@infrastructure/clients/http/utils";
import { logApiRequest, logApiResponse } from "@shared/utils/logger/apiLogger";

export interface RequestResponse<T = any> {
  status: string;
  data: T;
  details?: string;
}

export abstract class RestClient {
  abstract baseUrl: string;
  private _axiosInstance: AxiosInstance | null = null;
  private _lastRequestInfo: { method: string; endpoint: string; fullUrl: string } | null = null;

  protected get axiosInstance(): AxiosInstance {
    if (!this._axiosInstance) {
      if (this.baseUrl == null) {
        throw new Error("baseUrl must be set before using RestClient");
      }

      // Формируем базовые заголовки
      const defaultHeaders: Record<string, string> = { "Content-Type": "application/json" };

      this._axiosInstance = axios.create({
        baseURL: this.baseUrl,
        timeout: 60000,
        headers: defaultHeaders,
        withCredentials: false,
        validateStatus: (status) => status >= 200 && status < 300,
      });

      this._setupInterceptors();
    }
    return this._axiosInstance;
  }


  private _setupInterceptors(): void {
    // Request interceptor
    this._axiosInstance!.interceptors.request.use(
      (config) => {
        // Добавляем токен авторизации
        const token = authToken.get();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Для FormData удаляем Content-Type - axios установит его автоматически
        if (config.data instanceof FormData) {
          delete config.headers["Content-Type"];
        }

        const method = (config.method?.toUpperCase() || "GET") as string;
        const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url || "";

        // Логируем запрос с временем через общий логгер
        logApiRequest({
          method,
          url: config.url || "",
          baseURL: config.baseURL,
          fullURL: fullUrl,
          headers: config.headers as Record<string, any>,
          params: config.params,
          data: config.data,
        });

        // Логируем в аналитику
        const requestId = analytics.logApiRequestStart(config.url || "", method, {
          fullUrl,
          hasData: !!config.data,
          isFormData: config.data instanceof FormData,
        });
        (config as any).__analyticsRequestId = requestId;

        return config;
      },
      (error) => {
        logInterceptorError(error, "request");
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this._axiosInstance!.interceptors.response.use(
      async (response) => {
        const requestId = (response.config as any).__analyticsRequestId;
        const method = (response.config.method?.toUpperCase() || "GET") as string;
        
        // Логируем успешный ответ с временем
        logApiResponse({
          method,
          url: response.config.url || "",
          status: response.status,
          statusText: response.statusText,
          fullURL: response.config.baseURL && response.config.url 
            ? `${response.config.baseURL}${response.config.url}` 
            : undefined,
          data: response.data,
        });
        
        if (requestId) {
          analytics.logApiRequestEnd(
            requestId,
            response.config.url || "",
            method,
            response.status,
            {
              contentType: response.headers["content-type"],
              dataKeys: response.data ? Object.keys(response.data) : [],
            }
          );
        }
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config;
        
        if (!originalRequest) {
          return Promise.reject(error);
        }
        
        // Логируем ошибку через утилиту
        logHttpError(error, originalRequest);

        const method = (originalRequest.method?.toUpperCase() || "GET") as string;

        // Логируем ошибку в аналитику
        const requestId = (originalRequest as any)?.__analyticsRequestId;
        if (requestId) {
          analytics.logApiRequestError(
            requestId,
            originalRequest.url || "",
            method,
            error.response?.status
              ? `HTTP ${error.response.status}: ${error.response.statusText}`
              : error.message,
            {
              status: error.response?.status,
              statusText: error.response?.statusText,
            }
          );
        }

        // Обработка ошибки "initData expired" (500 с сообщением об истечении токена)
        if (error.response?.status === 500) {
          const errorData = error.response.data as any;
          const errorMessage = errorData?.data?.errors || errorData?.details || errorData?.message || "";
          const isInitDataExpired = typeof errorMessage === 'string' && 
            (errorMessage.includes("initData expired") || errorMessage.includes("expired"));
          
          if (isInitDataExpired) {
            console.warn("[RestClient] InitData expired detected in API response, reloading page");
            // Перезагружаем страницу для получения свежего токена от Telegram
            setTimeout(() => {
              window.location.reload();
            }, 1000);
            return Promise.reject(error);
          }
        }

        // Обработка 401 ошибки (unauthorized)
        if (error.response?.status === 401 && !(originalRequest as any)._retry) {
          (originalRequest as any)._retry = true;
          const isAuthEndpoint = originalRequest.url?.includes("/auth/");

          if (!isAuthEndpoint) {
            console.log("[RestClient] 401 Unauthorized detected, starting re-authentication...");
            
            // Обновляем состояние авторизации
            const currentAuthState = AuthStore.auth;
            if (currentAuthState.state === "authenticated") {
              console.log("[RestClient] User was authenticated, setting reauth state");
              AuthStore.setAuth({
                state: "reauth",
                previousUser: currentAuthState.user,
              });
            } else if (currentAuthState.state === "anonymous" && !currentAuthState.reauthRequired) {
              console.log("[RestClient] User was anonymous, setting reauthRequired");
              AuthStore.setAuth({ state: "anonymous", reauthRequired: true });
            }

            // Выполняем переавторизацию и ждем её завершения перед повторным запросом
            try {
              console.log("[RestClient] Waiting for re-authentication...");
              const reauthSuccess = await AuthService.reauth();
              console.log("[RestClient] Re-authentication result:", reauthSuccess);
              
              if (reauthSuccess) {
                const token = authToken.get();
                if (token && originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  console.log("[RestClient] Re-auth successful, retrying request:", originalRequest.url);
                  // Сбрасываем флаг _retry для повторного запроса
                  delete (originalRequest as any)._retry;
                  return this.axiosInstance.request(originalRequest);
                } else {
                  console.error("[RestClient] Re-auth successful but no token available");
                }
              } else {
                console.error("[RestClient] Re-auth failed, cannot retry request");
              }
            } catch (reauthError) {
              console.error("[RestClient] Re-auth error:", reauthError);
            }
          }
        }

        return Promise.reject(error);
      }
    );
  }

  protected async request(
    method: string,
    endpoint: string,
    options: {
      params?: Record<string, any>;
      jsonPayload?: any;
      formData?: FormData;
      headers?: Record<string, string>;
      signal?: AbortSignal;
    } = {}
  ): Promise<RequestResponse> {
    const { params, jsonPayload, formData, headers, signal } = options;

    try {
      const requestHeaders: Record<string, string> = { ...headers };
      const hasData = !!(formData || jsonPayload);

      // Управление Content-Type заголовком
      if (formData) {
        // Для FormData удаляем Content-Type - axios установит multipart/form-data с boundary
        delete requestHeaders["Content-Type"];
      } else if (!hasData && method.toUpperCase() === "POST") {
        // Для пустых POST запросов удаляем Content-Type для избежания проблем с CORS
        delete requestHeaders["Content-Type"];
      }

      const config: AxiosRequestConfig = {
        method: method as any,
        url: endpoint,
        params,
        data: hasData ? (formData || jsonPayload) : undefined,
        headers: requestHeaders,
        signal,
        withCredentials: false,
      };

      // Сохраняем информацию о запросе для логирования ошибок
      const fullRequestUrl = `${this.baseUrl}${endpoint}`;
      this._lastRequestInfo = {
        method: method.toUpperCase(),
        endpoint,
        fullUrl: fullRequestUrl,
      };

      const response = await this.axiosInstance.request(config);
      const responseData = response.data;

      // Проверка формата ответа (CommonDto)
      if (responseData && typeof responseData === "object" && "status" in responseData) {
        return responseData as RequestResponse;
      }

      // Если ответ не в формате CommonDto, оборачиваем его
      const wrappedResponse = {
        status: "success",
        data: responseData,
      };

      // Логируем, если data - строка (возможная проблема)
      if (typeof responseData === "string") {
        const isHtml = responseData.trim().startsWith("<!DOCTYPE");
        
        if (isHtml && this._lastRequestInfo) {
          logHtmlResponseWarning(response, {
            method: this._lastRequestInfo.method,
            fullUrl: fullRequestUrl,
          });
        }
      }

      return wrappedResponse;
    } catch (error: any) {
      const handledError = await handleError(error);
      throw handledError;
    }
  }

  protected validateResponse<TData>(
    response: RequestResponse,
    schema: z.ZodType<any>
  ): RequestResponse<TData> {
    // Логируем сырой ответ перед валидацией для отладки
    const endpoint = this._lastRequestInfo?.endpoint || "unknown";
    if (endpoint.includes("/orders/create")) {
      console.log("[RestClient.validateResponse] Raw response before validation:", JSON.stringify(response, null, 2));
    }
    
    const parsed = schema.safeParse(response);
    if (!parsed.success) {
      // Логируем ошибку валидации через утилиту
      logSchemaValidationError(
        response,
        parsed.error.errors,
        this._lastRequestInfo,
        schema
      );
      
      throw new Error(`Schema validation failed: ${parsed.error.message}`);
    }
    
    // Логируем успешно распарсенный ответ для отладки
    if (endpoint.includes("/orders/create")) {
      console.log("[RestClient.validateResponse] Parsed response:", JSON.stringify(parsed.data, null, 2));
    }
    
    return parsed.data as RequestResponse<TData>;
  }
}
